import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, MenuItem, TextField, Typography } from "@mui/material";
import { AlertTriangle, Plus, Save, Trash2, X } from "lucide-react";
import sh from "bundles/common/utils/sh";
import { missingSections } from "bundles/Internal/utils/brandedCv";

const LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2", "native"];

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    fontSize: 12.5,
    borderRadius: "8px",
    backgroundColor: "#ffffff",
  },
  "& .MuiInputLabel-root": { fontSize: 12.5 },
  "& .MuiOutlinedInput-input": { py: 0.9 },
};

const SectionTitle = ({ children, missing }) => (
  <Typography
    sx={{
      mt: 2,
      mb: 0.75,
      display: "flex",
      alignItems: "center",
      gap: 0.5,
      fontSize: 10.5,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      color: missing ? "hsl(35 60% 32%)" : "hsl(150 10% 46%)",
      fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
    }}
  >
    {children}
    {missing && <AlertTriangle size={11} />}
  </Typography>
);

const iconButtonSx = {
  minWidth: 0,
  p: 0.5,
  borderRadius: "6px",
  color: "hsl(150 10% 55%)",
  "&:hover": { color: "hsl(0 72% 51%)" },
};

const addButtonSx = {
  mt: 0.75,
  textTransform: "none",
  fontSize: 12,
  borderRadius: "8px",
  color: "hsl(150 30% 12%)",
  border: "1px dashed hsl(140 8% 80%)",
  "&:hover": { backgroundColor: "hsl(140 10% 96%)" },
};

const Card = ({ children, onRemove }) => (
  <Box
    sx={{
      position: "relative",
      p: 1,
      mb: 1,
      borderRadius: "10px",
      border: "1px solid hsl(140 8% 90%)",
      backgroundColor: "hsl(140 10% 98%)",
    }}
  >
    <Button size="small" disableRipple onClick={onRemove} sx={{ ...iconButtonSx, position: "absolute", top: 4, right: 4 }}>
      <Trash2 size={13} />
    </Button>
    {children}
  </Box>
);

// A list of plain strings — skills, certifications, highlights. One row per entry so long
// sentences stay readable; chips would truncate the highlights.
const StringList = ({ values, onChange, placeholder }) => (
  <Box>
    {(values || []).map((value, index) => (
      <Box key={index} sx={{ display: "flex", gap: 0.5, mb: 0.5 }}>
        <TextField
          fullWidth
          size="small"
          multiline
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(values.map((v, i) => (i === index ? e.target.value : v)))}
          sx={fieldSx}
        />
        <Button
          size="small"
          disableRipple
          onClick={() => onChange(values.filter((_, i) => i !== index))}
          sx={iconButtonSx}
        >
          <Trash2 size={13} />
        </Button>
      </Box>
    ))}
    <Button size="small" disableRipple startIcon={<Plus size={13} />} onClick={() => onChange([...(values || []), ""])} sx={addButtonSx}>
      Add
    </Button>
  </Box>
);

const BrandedCvEditor = ({ sourceUrl, data, onSaved, onClose }) => {
  const [draft, setDraft] = useState(data || {});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => setDraft(data || {}), [data]);

  const missing = new Set(missingSections(draft).map((section) => section.key));

  const set = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));
  const setContact = (key, value) =>
    setDraft((prev) => ({ ...prev, contact: { ...(prev.contact || {}), [key]: value } }));

  const setRow = (collection, index, key, value) =>
    setDraft((prev) => ({
      ...prev,
      [collection]: prev[collection].map((row, i) => (i === index ? { ...row, [key]: value } : row)),
    }));

  const removeRow = (collection, index) =>
    setDraft((prev) => ({ ...prev, [collection]: prev[collection].filter((_, i) => i !== index) }));

  const addRow = (collection, row) =>
    setDraft((prev) => ({ ...prev, [collection]: [...(prev[collection] || []), row] }));

  const save = () => {
    setSaving(true);
    setError(null);

    sh.patch(sourceUrl, { data: draft }, { timeout: 60000 })
      .then((res) => {
        setSaving(false);
        onSaved(res.data);
        onClose();
      })
      .catch((err) => {
        setSaving(false);
        setError(err?.response?.data?.error || "Could not save. The document was not changed.");
      });
  };

  const text = (label, key, extra = {}) => (
    <TextField
      fullWidth
      size="small"
      label={label}
      value={draft[key] || ""}
      onChange={(e) => set(key, e.target.value)}
      sx={fieldSx}
      {...extra}
    />
  );

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        backgroundColor: "#ffffff",
      }}
    >
      <Box
        sx={{
          px: 1.5,
          height: 40,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: "1px solid hsl(140 8% 88%)",
        }}
      >
        <Typography sx={{ flex: 1, fontSize: 13, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>
          Edit the Squarehub CV
        </Typography>
        <Button
          size="small"
          disableRipple
          disabled={saving}
          startIcon={saving ? <CircularProgress size={12} sx={{ color: "inherit" }} /> : <Save size={13} />}
          onClick={save}
          sx={{
            textTransform: "none",
            fontSize: 12.5,
            height: 32,
            borderRadius: "8px",
            px: 1.25,
            color: "hsl(80 97% 72%)",
            backgroundColor: "hsl(150 38% 17%)",
            "&:hover": { backgroundColor: "hsl(150 38% 22%)" },
            "&.Mui-disabled": { color: "hsl(80 40% 80%)", backgroundColor: "hsl(150 20% 40%)" },
          }}
        >
          {saving ? "Rendering" : "Save"}
        </Button>
        <Button size="small" disableRipple onClick={onClose} sx={{ ...iconButtonSx, "&:hover": { color: "hsl(150 30% 12%)" } }}>
          <X size={15} />
        </Button>
      </Box>

      {error && (
        <Typography sx={{ px: 1.5, py: 0.75, fontSize: 11.5, color: "hsl(0 55% 38%)", backgroundColor: "hsl(0 80% 98%)" }}>
          {error}
        </Typography>
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 1.5, pb: 3 }}>
        <SectionTitle missing={missing.has("role")}>Identity</SectionTitle>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {text("Full name", "name")}
          {text("Role", "role")}
        </Box>

        <SectionTitle>Contact</SectionTitle>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
          {["email", "phone", "address", "permit"].map((key) => (
            <TextField
              key={key}
              size="small"
              label={key[0].toUpperCase() + key.slice(1)}
              value={draft.contact?.[key] || ""}
              onChange={(e) => setContact(key, e.target.value)}
              sx={fieldSx}
            />
          ))}
          {text("Mobility", "mobility")}
        </Box>

        <SectionTitle missing={missing.has("profile")}>Profile</SectionTitle>
        {text("Profile", "profile", { multiline: true, minRows: 4, label: "" })}

        <SectionTitle missing={missing.has("experience")}>Experience</SectionTitle>
        {(draft.experience || []).map((row, index) => (
          <Card key={index} onRemove={() => removeRow("experience", index)}>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.75, pr: 3 }}>
              <TextField size="small" label="Title" value={row.title || ""} onChange={(e) => setRow("experience", index, "title", e.target.value)} sx={fieldSx} />
              <TextField size="small" label="Company" value={row.company || ""} onChange={(e) => setRow("experience", index, "company", e.target.value)} sx={fieldSx} />
              <TextField size="small" label="Location" value={row.location || ""} onChange={(e) => setRow("experience", index, "location", e.target.value)} sx={fieldSx} />
              <Box sx={{ display: "flex", gap: 0.75 }}>
                <TextField size="small" label="From" placeholder="2023-09" value={row.start_date || ""} onChange={(e) => setRow("experience", index, "start_date", e.target.value)} sx={fieldSx} />
                <TextField size="small" label="To" placeholder="ongoing" value={row.end_date || ""} onChange={(e) => setRow("experience", index, "end_date", e.target.value)} sx={fieldSx} />
              </Box>
            </Box>
            <Box sx={{ mt: 0.75 }}>
              <StringList
                values={row.bullets}
                placeholder="Responsibility"
                onChange={(bullets) => setRow("experience", index, "bullets", bullets)}
              />
            </Box>
          </Card>
        ))}
        <Button size="small" disableRipple startIcon={<Plus size={13} />} sx={addButtonSx} onClick={() => addRow("experience", { title: "", company: "", bullets: [] })}>
          Add experience
        </Button>

        <SectionTitle missing={missing.has("education")}>Education</SectionTitle>
        {(draft.education || []).map((row, index) => (
          <Card key={index} onRemove={() => removeRow("education", index)}>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.75, pr: 3 }}>
              <TextField size="small" label="Diploma" value={row.title || ""} onChange={(e) => setRow("education", index, "title", e.target.value)} sx={fieldSx} />
              <TextField size="small" label="Institution" value={row.institution || ""} onChange={(e) => setRow("education", index, "institution", e.target.value)} sx={fieldSx} />
              <TextField size="small" label="From" placeholder="1986-09" value={row.start_date || ""} onChange={(e) => setRow("education", index, "start_date", e.target.value)} sx={fieldSx} />
              <TextField size="small" label="To" value={row.end_date || ""} onChange={(e) => setRow("education", index, "end_date", e.target.value)} sx={fieldSx} />
            </Box>
          </Card>
        ))}
        <Button size="small" disableRipple startIcon={<Plus size={13} />} sx={addButtonSx} onClick={() => addRow("education", { title: "", institution: "" })}>
          Add education
        </Button>

        <SectionTitle>Languages</SectionTitle>
        {(draft.languages || []).map((row, index) => (
          <Box key={index} sx={{ display: "flex", gap: 0.5, mb: 0.5 }}>
            <TextField size="small" fullWidth label="Language" value={row.name || ""} onChange={(e) => setRow("languages", index, "name", e.target.value)} sx={fieldSx} />
            <TextField select size="small" label="Level" value={row.level || "B1"} onChange={(e) => setRow("languages", index, "level", e.target.value)} sx={{ ...fieldSx, minWidth: 110 }}>
              {LEVELS.map((level) => (
                <MenuItem key={level} value={level} sx={{ fontSize: 12.5 }}>{level}</MenuItem>
              ))}
            </TextField>
            <Button size="small" disableRipple onClick={() => removeRow("languages", index)} sx={iconButtonSx}>
              <Trash2 size={13} />
            </Button>
          </Box>
        ))}
        <Button size="small" disableRipple startIcon={<Plus size={13} />} sx={addButtonSx} onClick={() => addRow("languages", { name: "", level: "B1" })}>
          Add language
        </Button>

        <SectionTitle>Areas of expertise</SectionTitle>
        <StringList values={draft.skills} placeholder="Skill" onChange={(v) => set("skills", v)} />

        <SectionTitle>Soft skills</SectionTitle>
        <StringList values={draft.soft_skills} placeholder="Soft skill" onChange={(v) => set("soft_skills", v)} />

        <SectionTitle>Certifications</SectionTitle>
        <StringList values={draft.certifications} placeholder="Certification" onChange={(v) => set("certifications", v)} />

        <SectionTitle>Key strengths</SectionTitle>
        <StringList values={draft.highlights} placeholder="Strength" onChange={(v) => set("highlights", v)} />
      </Box>
    </Box>
  );
};

export default BrandedCvEditor;
